import Decimal from 'decimal.js';
import { PRICE_DP, QTY_DP } from './quote-calc';

/**
 * Quy đổi ĐVT (DEC-029 B; dùng trên báo giá ở P06 – DEC-073 A).
 * Quy ước giữ đúng migration 0005/0026 và file Asia: 1 ĐVT chính = factor × ĐVT phụ.
 * Ví dụ ống kẽm D114x2.6: 1 Cây = 42,66 Kg → 7 Cây = 298,62 Kg.
 * Làm tròn ROUND_HALF_UP theo cột DB: số lượng 3 số lẻ, đơn giá 2 số lẻ, hệ số 6 số lẻ.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
/** Hệ số product_uoms.factor numeric(18,6). */
export const FACTOR_DP = 6;

function factorOf(factor: number | string): Decimal {
  const f = new D(factor).toDecimalPlaces(FACTOR_DP);
  if (!f.isFinite() || f.lte(0)) throw new Error('Hệ số quy đổi phải lớn hơn 0');
  return f;
}

/** Số lượng theo ĐVT chính → số lượng theo ĐVT phụ. */
export function toAuxQty(baseQty: number | string, factor: number | string): number {
  return new D(baseQty).toDecimalPlaces(QTY_DP).mul(factorOf(factor)).toDecimalPlaces(QTY_DP).toNumber();
}

/** Số lượng theo ĐVT phụ → số lượng theo ĐVT chính. */
export function toBaseQty(auxQty: number | string, factor: number | string): number {
  return new D(auxQty).toDecimalPlaces(QTY_DP).div(factorOf(factor)).toDecimalPlaces(QTY_DP).toNumber();
}

/** Đơn giá theo ĐVT chính → đơn giá theo ĐVT phụ (giá / factor). */
export function toAuxPrice(basePrice: number | string, factor: number | string): number {
  return new D(basePrice).toDecimalPlaces(PRICE_DP).div(factorOf(factor)).toDecimalPlaces(PRICE_DP).toNumber();
}

/** Đơn giá theo ĐVT phụ → đơn giá theo ĐVT chính (giá × factor). */
export function toBasePrice(auxPrice: number | string, factor: number | string): number {
  return new D(auxPrice).toDecimalPlaces(PRICE_DP).mul(factorOf(factor)).toDecimalPlaces(PRICE_DP).toNumber();
}

// ---------------------------------------------------------------------------
// P06 B3: chọn ĐVT trên dòng báo giá (DEC-073 A) + thép tấm (DEC-107 A)
// ---------------------------------------------------------------------------

export interface UomOption {
  uom: string;
  /** 1 ĐVT chính = factor × uom; ĐVT chính có factor 1 */
  factor: number;
  isBase: boolean;
}

const normUnit = (u: string) => u.trim().toUpperCase();
export const sameUnit = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && normUnit(a) === normUnit(b);

/** ĐVT kg (Asia ghi "Kg", "KG", "kg"...). */
export const isKgUnit = (u: string | null | undefined) => !!u && /^(kg|kgs|kilogam|kilogram)$/i.test(u.trim());

/**
 * Danh sách ĐVT bán được của 1 sản phẩm: ĐVT chính + ĐVT phụ hợp lệ (product_uoms).
 * ĐVT Asia lỗi (ZERO_FACTOR, DUPLICATE_NAME) không bao giờ vào product_uoms (asia.service mapAsiaUnits),
 * nên không có trong danh sách và không chọn được. Hệ số ≤ 0 hoặc trùng ĐVT chính cũng bị bỏ.
 */
export function uomOptions(baseUnit: string, uoms: { uom: string; factor: number | string }[] = []): UomOption[] {
  const out: UomOption[] = [{ uom: baseUnit, factor: 1, isBase: true }];
  for (const u of uoms) {
    const f = Number(u.factor);
    if (!u.uom?.trim() || !(f > 0) || out.some((o) => sameUnit(o.uom, u.uom))) continue;
    out.push({ uom: u.uom.trim(), factor: f, isBase: false });
  }
  return out;
}

/** ĐVT kg của sản phẩm (ĐVT chính là kg → factor 1; hoặc ĐVT phụ kg). null = không quy đổi được ra kg. */
export function kgOption(options: UomOption[]): UomOption | null {
  return options.find((o) => isKgUnit(o.uom)) ?? null;
}

/**
 * DEC-331 (anh Tiến 14/10/2026 "nhóm Sắt thép, có đơn vị Kg - cây - mét: mặc định bán ký"): nhóm hàng bật "bán theo kg"
 * và mã có ĐVT phụ Kg → ĐVT bán mặc định trên báo giá là Kg. Chỉ là mặc định của dòng mới: ĐVT chính, tồn kho, dữ liệu Asia
 * vẫn theo cây / mét; NV vẫn đổi được ĐVT trên dòng. null = bán theo ĐVT chính (ĐVT chính đã là Kg cũng trả null).
 */
export function defaultSaleUom(options: UomOption[], sellByKg: boolean | null | undefined): UomOption | null {
  if (!sellByKg) return null;
  const kg = kgOption(options);
  return kg && !kg.isBase ? kg : null;
}

/** Đổi đơn giá khi NV đổi ĐVT trên dòng: quy về ĐVT chính rồi sang ĐVT mới. */
export function convertUnitPrice(price: number | string, fromFactor: number | string, toFactor: number | string): number {
  return toAuxPrice(toBasePrice(price, fromFactor), toFactor);
}

/** Khối lượng riêng (g/cm³ = kg / (m² × mm)) theo DEC-107 A. */
export const STEEL_DENSITY = 7.85;
export const STAINLESS_DENSITY = 7.93;

/** Thép tấm: chiều dài, rộng (m) 3 số lẻ, dày 2 số lẻ, khối lượng riêng 3 số lẻ (cột quote_items). */
export const PLATE_DIM_DP = 3;
export const PLATE_THICKNESS_DP = 2;
export const PLATE_DENSITY_DP = 3;

export interface PlateInput {
  count: number | string;
  lengthM: number | string;
  widthM: number | string;
  thicknessMm: number | string;
  density: number | string;
}

/**
 * DEC-107 A: kg = số tấm × dài (m) × rộng (m) × dày (mm) × khối lượng riêng; làm tròn 3 số lẻ (cột quantity).
 * VD 2 tấm 6 m × 1,5 m dày 10 mm thép thường: 2 × 6 × 1,5 × 10 × 7,85 = 1.413 kg.
 */
export function plateKg(p: PlateInput): number {
  const count = new D(p.count);
  if (!count.isInteger() || count.lte(0)) throw new Error('Số tấm phải là số nguyên lớn hơn 0');
  const l = new D(p.lengthM).toDecimalPlaces(PLATE_DIM_DP);
  const w = new D(p.widthM).toDecimalPlaces(PLATE_DIM_DP);
  const t = new D(p.thicknessMm).toDecimalPlaces(PLATE_THICKNESS_DP);
  const rho = new D(p.density).toDecimalPlaces(PLATE_DENSITY_DP);
  if (l.lte(0) || w.lte(0) || t.lte(0) || rho.lte(0)) throw new Error('Dài, rộng, dày, khối lượng riêng phải lớn hơn 0');
  return count.mul(l).mul(w).mul(t).mul(rho).toDecimalPlaces(QTY_DP).toNumber();
}

/** Inox (nhóm thuộc tính stainless, hoặc tên/mác có INOX, SUS) dùng 7,93; còn lại 7,85. */
export function plateDensity(p: { attrGroup?: string | null; name?: string | null; grade?: string | null; material?: string | null }): number {
  if (p.attrGroup === 'stainless') return STAINLESS_DENSITY;
  const text = [p.name, p.grade, p.material].filter(Boolean).join(' ');
  return /\b(inox|sus\s?\d{3})/i.test(text) ? STAINLESS_DENSITY : STEEL_DENSITY;
}

// ---------------------------------------------------------------------------
// Tính theo tấm cho mã chưa có cột độ dày (anh Tiến 07/10/2026): Asia để "Độ dày" = 0 ở gần hết mã thép tấm
// (nhóm THEPTAM: "Thép Tấm 3.0mm", "Tấm inox 304 - 1220*3050*1.0mm"), nên lấy độ dày từ tên, không có thì NV nhập tay.
// ---------------------------------------------------------------------------

/** Độ dày tấm hợp lệ (mm): 0,1 … 200. */
export const PLATE_THICKNESS_MIN = 0.1;
export const PLATE_THICKNESS_MAX = 200;

/** Tên hàng dạng tấm/lá/tôn (không nhầm "bê tông", "tâm"). */
export function isPlateName(name: string | null | undefined): boolean {
  return !!name && /(tấm|thép\s*lá|tôn(?!\p{L}))/iu.test(name);
}

const validThickness = (t: number) => (t >= PLATE_THICKNESS_MIN && t <= PLATE_THICKNESS_MAX ? new D(t).toDecimalPlaces(PLATE_THICKNESS_DP).toNumber() : null);

/**
 * Độ dày (mm) đọc từ tên tấm: số đứng ngay trước "mm"/"ly"/"li" (lấy số cuối, bỏ phần trong ngoặc như "(đục lỗ 6mm)"),
 * hoặc "dem"/"zem" (1 dem = 0,1 mm). Không đọc được → null.
 * VD "Tấm inox 304 - 1220*3050*1.0mm" → 1; "Thép Tấm 10ly" → 10; "Tôn NB - 3.5 dem" → 0,35.
 */
export function thicknessFromName(name: string | null | undefined): number | null {
  if (!name) return null;
  const text = name.replace(/\([^)]*\)/g, ' ').replace(/(\d),(\d)/g, '$1.$2');
  const mm = [...text.matchAll(/(\d+(?:\.\d+)?)\s*(?:mm|ly|li)(?!\p{L})/giu)];
  if (mm.length) return validThickness(Number(mm[mm.length - 1]![1]));
  const dem = /(\d+(?:\.\d+)?)\s*(?:dem|zem)(?!\p{L})/iu.exec(text);
  return dem ? validThickness(Number(dem[1]) / 10) : null;
}

export interface PlateSpec {
  /** null = mã chưa có độ dày, NV nhập tay trên dòng */
  thicknessMm: number | null;
  density: number;
}

/**
 * Mã tính được theo tấm (DEC-107 A) khi có ĐVT Kg và: có cột độ dày, hoặc tên là tấm/tôn/lá
 * (độ dày lấy từ tên; không đọc được thì thicknessMm = null → nhập tay).
 */
export function productPlateSpec(p: {
  unit: string | null | undefined;
  uoms?: { uom: string; factor: number | string }[] | null;
  thicknessMm?: number | string | null;
  name?: string | null;
  attrGroup?: string | null;
  grade?: string | null;
  material?: string | null;
}): PlateSpec | null {
  if (!p.unit || !kgOption(uomOptions(p.unit, p.uoms ?? []))) return null;
  const col = p.thicknessMm == null ? 0 : Number(p.thicknessMm);
  const plateName = isPlateName(p.name);
  if (!(col > 0) && !plateName) return null;
  return {
    thicknessMm: col > 0 ? col : thicknessFromName(p.name),
    density: plateDensity({ attrGroup: p.attrGroup, name: p.name, grade: p.grade, material: p.material }),
  };
}
